import {HttpFatal} from './errors/baseHandling/HttpFatal'
import {Messages} from '../Messages'
import {HttpCritical} from './errors/baseHandling/HttpCritical'
import {ErrorResponseBody} from './response/ErrorResponseBody'
import {HttpError} from './errors/HttpError'
import {ContentType} from './Constants'
import {HttpTimeoutError} from './errors/baseHandling/HttpTimeoutError'
import {MESSAGE_TYPE} from '../CodeMaster'
import {MaintenanceError} from './errors/MaintenanceError'
import {SessionTimeOutError} from './errors/SessionTimeOutError'
import {HKConstants} from '../HKConstants'
import {Optional} from '../app/TypeHelpers'
import {isIE} from 'react-device-detect'

const MAINTENANCE_HTTP_STATUS: number = 503
const WARNING_HTTP_STATUS: number = 412
const CSRF_COOKIE_NAME: string = '_csrf_token'

interface Body {
    [key: string]: any
}

function fetchWrapper(input: RequestInfo, init?: RequestInit): Promise<Response> {
    return fetch(input, init)
        .catch(() => {
            // noinspection UnnecessaryLocalVariableJS
            const networkError: HttpTimeoutError =
                new HttpTimeoutError({
                    requestError: {
                        messageId: Messages.ESYCA0002.id,
                        message: Messages.ESYCA0002.toString(),
                        title: Messages.ESYCA0001.toString(),
                        type: Messages.ESYCA0001.errorLevel,
                    },
                    requestItemErrors: [],
                })
            throw networkError
        })
}


export default class Http {
    async get(url: RequestInfo): Promise<any> {
        const response: Response = await fetchWrapper(url, {
            method: 'GET',
        })
        return await this.getResponseJson(response)
    }

    async postFetch(url: RequestInfo, requestBody: string, requestContentType: string): Promise<Response> {
        return fetchWrapper(url, {
            method: 'POST',
            body: requestBody,
            headers: {
                'Content-Type': `${requestContentType}; charset=utf-8`,
                'X-CSRF-TOKEN': getCsrfToken(),
            },
        })
    }

    async getJsonFromBody(response: Response): Promise<any> {
        try {
            return await response.json()
        } catch (bodyJsonNotFoundException) {
            return null
        }
    }

    getRequestErrorType(json: any, status: number): string {
        if (!json) {
            throw new HttpFatal(status, null)
        }
        if (!json.requestError) {
            throw new HttpFatal(status, null)
        }
        return json.requestError.type
    }

    async getResponseJson(response: Response): Promise<any> {
        const json: any = await this.getJsonFromBody(response)

        if (MAINTENANCE_HTTP_STATUS === response.status) {
            throw new MaintenanceError()
        }

        if (HKConstants.CMN.SYC.HTTP_STATUS_SESSION_TIMEOUT === response.status) {
            throw new SessionTimeOutError()
        }

        if (!response.ok) {
            if (WARNING_HTTP_STATUS !== response.status) {
                const requestErrorType: string = this.getRequestErrorType(json, response.status)
                switch (requestErrorType) {
                    case MESSAGE_TYPE.ERROR.value:
                        throw new HttpError(response.status, ErrorResponseBody.from(json))
                    case MESSAGE_TYPE.CRITICAL.value:
                        throw new HttpCritical(response.status, ErrorResponseBody.from(json))
                    default:
                        throw new HttpFatal(response.status, null)
                }
            }
        }

        return await json
    }

    async post(url: RequestInfo, body: Body, type: ContentType): Promise<any> {
        let requestBody: string = ''
        let requestContentType: string = ''
        switch (type) {
            case ContentType.FORM:
                requestBody = createFormBody(body)
                requestContentType = 'application/x-www-form-urlencoded'
                break
            case ContentType.JSON:
                if (isIE) {
                    requestBody = JSON.stringify(body)
                } else {
                    requestBody = JSON.stringify(body).normalize('NFC')
                }
                requestContentType = 'application/json'
                break
        }
        const response: Response = await this.postFetch(url, requestBody, requestContentType)
        return await this.getResponseJson(response)
    }

    async put(url: RequestInfo, body: Body): Promise<any> {
        let requestBody: string = ''
        if (isIE) {
            requestBody = JSON.stringify(body)
        } else {
            requestBody = JSON.stringify(body).normalize('NFC')
        }

        const response: Response = await fetchWrapper(url, {
            method: 'PUT',
            body: requestBody,
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
        })
        return await this.getResponseJson(response)
    }

    async delete(url: RequestInfo): Promise<any> {
        const response: Response = await fetchWrapper(url, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': getCsrfToken(),
            },
        })
        return await this.getResponseJson(response)
    }

    async patch(url: RequestInfo, body: Body): Promise<any> {
        let requestBody: string = ''
        if (isIE) {
            requestBody = JSON.stringify(body)
        } else {
            requestBody = JSON.stringify(body).normalize('NFC')
        }

        const response: Response = await fetchWrapper(url, {
            method: 'PATCH',
            body: requestBody,
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
        })
        return await this.getResponseJson(response)
    }

    async getDataUrl(url: RequestInfo): Promise<string | null> {
        const response: Response = await fetch(url)
        return response.blob().then(async (blob) => {
            if (blob === undefined) {
                return null
            }

            const reader = new FileReader()
            reader.readAsDataURL(blob)
            return await new Promise((resolve, reject) => {
                reader.onload = () => {
                    if (!reader.result) {
                        reject(null)
                    }
                    resolve(reader.result as string)
                }
            })
        })
    }

    async getHtmlContents(url: RequestInfo): Promise<any> {
        const response: Response = await fetch(url)
        return response.text()
    }
}

function createFormBody(body: Body): string {
    const bodyKeys: string[] = Object.keys(body)
    const formRows: string[] = bodyKeys.map(key => `${key}=${body[key]}`)
    return formRows.join('&')
}

function getCsrfToken(): string {
    let csrfToken: Optional<string>
    document.cookie.split(';')
        .forEach(keyValue => {
            const keyValueArray = keyValue.trim().split('=')
            if (keyValueArray.length === 2 && keyValueArray[0] === CSRF_COOKIE_NAME) {
                csrfToken = keyValueArray[1]
            }
        })
    return csrfToken || ''
}
